import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	OutlinedInput,
	FormHelperText,
} from '@mui/material';

import {
	IAnnotationFormData,
	IAnnotationFormProps,
} from 'src/features/annotation/domain/types';
import { annotationLabelAssignmentSchema } from 'src/features/annotation/domain/validation';
import { updateAnnotation } from 'src/features/annotation/store';
import { AppDispatch } from 'src/store';

const AnnotationForm = (props: IAnnotationFormProps) => {
	const {
		disabled,
		currentAnnotation,
		isLoading,
		onChange,
		annotationLabels,
		onSubmit,
		shouldSave,
		onValidationError,
		onCurrentAnnotationUpdate,
	} = props;

	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();

	const {
		control,
		handleSubmit,
		formState: { isDirty, errors, isValid },
		reset,
	} = useForm<IAnnotationFormData>({
		mode: 'all',
		resolver: yupResolver(annotationLabelAssignmentSchema),
		defaultValues: {
			remarks: '',
			annotation_label_ids: [],
		},
	});

	const handleSave = async (
		data: IAnnotationFormData,
	): Promise<string | undefined> => {
		if (!isDirty) return undefined;
		if (!currentAnnotation || !siteSlug) {
			return 'Form failed to save';
		}

		const updatedData = {
			remarks: data.remarks,
			annotation_label_ids: data.annotation_label_ids,
		};

		try {
			await dispatch(
				updateAnnotation({
					annotationId: currentAnnotation.id,
					request: updatedData,
					siteSlug,
					onSuccess: (annotation) => {
						reset({
							remarks: annotation.remarks,
							annotation_label_ids:
								annotation.annotation_label_ids,
						});
						onCurrentAnnotationUpdate(annotation);
					},
				}),
			).unwrap();

			return 'Form saved successfully';
		} catch (error) {
			console.error(error);
			return 'Form failed to save due to unexpected error';
		}
	};

	useEffect(() => {
		reset({
			remarks: currentAnnotation?.remarks ?? '',
			annotation_label_ids:
				currentAnnotation?.annotation_label_ids?.map((id) => id) ?? [],
		});
	}, [currentAnnotation, reset]);

	useEffect(() => {
		onChange(isDirty);
	}, [isDirty]);

	useEffect(() => {
		onValidationError(!isValid);
	}, [isValid]);

	useEffect(() => {
		if (!shouldSave.shouldSave) return;
		handleSubmit((data) => onSubmit(handleSave(data)))();
	}, [shouldSave]);

	return (
		<form>
			<Box display='flex' flexDirection='column' gap={2}>
				<Controller
					name='annotation_label_ids'
					control={control}
					render={({ field }) => (
						<FormControl fullWidth required>
							<InputLabel error={!!errors.annotation_label_ids}>
								Diagnosis
							</InputLabel>
							<Select
								{...field}
								multiple
								error={!!errors.annotation_label_ids}
								input={
									<OutlinedInput
										label='Diagnosis'
										error={!!errors.annotation_label_ids}
									/>
								}
								MenuProps={{
									PaperProps: {
										style: {
											maxHeight: 250,
											overflowY: 'auto',
											scrollbarWidth: 'thin',
										},
									},
								}}
								renderValue={(selected) => (
									<Box
										sx={{
											display: 'flex',
											flexWrap: 'wrap',
											gap: 0.5,
											maxHeight: 70,
											overflowY: 'auto',
											scrollbarWidth: 'thin',
										}}
									>
										{selected.map((id) => {
											const label = annotationLabels.find(
												(l) => l.id === Number(id),
											);

											return (
												<Chip
													key={id}
													label={label?.name ?? id}
												/>
											);
										})}
									</Box>
								)}
								disabled={disabled || isLoading}
							>
								{annotationLabels.map((label) => (
									<MenuItem key={label.id} value={label.id}>
										{label.name}
									</MenuItem>
								))}
							</Select>
							<FormHelperText
								error={!!errors.annotation_label_ids?.message}
							>
								{errors.annotation_label_ids?.message}
							</FormHelperText>
						</FormControl>
					)}
				/>

				<Controller
					name='remarks'
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							label='Remarks'
							fullWidth
							multiline
							minRows={3}
							maxRows={3}
							disabled={disabled || isLoading}
							error={!!errors.remarks}
							helperText={errors.remarks?.message}
						/>
					)}
				/>
			</Box>
		</form>
	);
};

export default AnnotationForm;
